import { __exportAll } from "./_virtual/_rolldown/runtime.js";
//#region src/logger.ts
var logger_exports = /* @__PURE__ */ __exportAll({ createLogger: () => createLogger });
var LOG_LEVEL_MAP = /* @__PURE__ */ new Map([
	["debug", 0],
	["info", 1],
	["warn", 2],
	["error", 3]
]);
/**
* Configurable Logger class.
*
* In library code (pd-utils), environment variables are not available at runtime.
* The host application should pass the configuration when creating a logger.
*
* @example
* ```typescript
* // In host application
* import { createLogger } from "@progressive-development/pd-utils";
*
* const logger = createLogger("MyApp", {
*   level: import.meta.env.VITE_LOG_LEVEL || "info",
*   isDev: import.meta.env.MODE === "development"
* });
*
* logger.debug("Debug message"); // Only shown if isDev or level <= "debug"
* logger.info("Info message");
* logger.warn("Warning message");
* logger.error("Error message");
* ```
*/
var LoggerImpl = class {
	constructor(context = "", options = {}) {
		this.context = context;
		this.level = options.level ?? "info";
		this.isDev = options.isDev ?? false;
	}
	shouldLog(level) {
		if (this.isDev) return true;
		return (LOG_LEVEL_MAP.get(level) ?? 0) >= (LOG_LEVEL_MAP.get(this.level) ?? 3);
	}
	log(level, message, ...args) {
		if (!this.shouldLog(level)) return;
		const prefix = `[${(/* @__PURE__ */ new Date()).toISOString()}] [${this.context}]`;
		switch (level) {
			case "debug":
				console.debug(prefix, message, ...args);
				break;
			case "info":
				console.info(prefix, message, ...args);
				break;
			case "warn":
				console.warn(prefix, message, ...args);
				break;
			case "error": console.error(prefix, message, ...args);
		}
	}
	debug(msg, ...args) {
		this.log("debug", msg, ...args);
	}
	info(msg, ...args) {
		this.log("info", msg, ...args);
	}
	warn(msg, ...args) {
		this.log("warn", msg, ...args);
	}
	error(msg, ...args) {
		this.log("error", msg, ...args);
	}
};
/**
* Creates a new Logger instance with the specified context and options.
*
* @param context - Prefix for log messages (e.g., component or module name)
* @param options - Logger configuration
* @returns A new Logger instance
*/
var createLogger = (context, options) => new LoggerImpl(context, options);
//#endregion
export { createLogger, logger_exports };
